import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import css from './GetStartedMarketingPage.css';
import config from '../../config';
import PageWrapper from '../../custom-components/wrappers/page-wrapper/PageWrapper';
import { coachLockupLogo } from '../../assets/viveka-logos';
import { isValidPostalCode } from '../../util/validators';
import getCountryCodes from '../../translations/countryCodes';

// Standalone landing + checkout page for the Lifetime plan's 3-monthly-
// installments offer (First-Mover-Lifetime-Installments-Monthly). Built as
// a self-contained sibling to CheckoutPlanPage.js rather than a
// modification of it or of planCatalog.js — this page owns its own copy,
// pricing, and Chargebee Payment Components integration so the existing
// /get-started -> /checkout-plan/:tier flow (full-price Lifetime included)
// is completely unaffected by this feature. The Payment Components
// mount/confirm pattern below follows CheckoutPlanPage.js's proven
// integration (see that file's history for why it's structured this way),
// written fresh here rather than imported/shared.

const PLAN_ID = 'First-Mover-Lifetime-Installments-Monthly';
// Must match the Chargebee item price's actual configured amount
// (First-Mover-Lifetime-Installments-Monthly-USD-Monthly) — these are
// display labels only, Chargebee's own price is the source of truth for
// what's actually authorized/charged (see /payment-intent).
const INSTALLMENT_AMOUNT_LABEL = '$359';
const TOTAL_LABEL = '$1,077';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const COMPONENT_LOAD_TIMEOUT_MS = 6000;
const COUNTRIES = getCountryCodes(config.locale);

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  addressLine1: '',
  city: '',
  state: '',
  zip: '',
  country: 'US',
};

const FEATURES = [
  {
    title: 'Professional marketplace profile',
    detail: 'a credentialed presence that positions you the way your work deserves, visible to the Viveka community',
  },
  {
    title: 'Resource library and community',
    detail: 'access ongoing resources and connect with other coaches building real, professional practices',
  },
  {
    title: 'Scheduling and booking',
    detail: 'manage your calendar and client bookings without juggling separate tools',
  },
  {
    title: 'Accountability tracker',
    detail: 'keep clients on track between sessions with built-in progress tools',
  },
  {
    title: 'Program management',
    detail: 'build and run structured coaching programs directly on the platform',
  },
  {
    title: '10 active listings',
    detail: 'showcase up to 10 programs or services at a time in the marketplace',
  },
  {
    title: 'Viveka Verification badge',
    detail: 'signals to organizations and clients that your credentials have been confirmed',
  },
  {
    title: 'Priority search placement',
    detail: "your profile is prioritized ahead of standard listings, so the right people see you first",
  },
  {
    title: 'Lifetime status, no renewals',
    detail: 'once your 3 payments are complete, your access never expires and never requires another payment',
  },
  {
    title: 'Locked-in price, no future increases',
    detail: "whatever Verified Coach costs down the road, you'll never pay it. Your rate is frozen the day you join",
  },
];

function GetStartedMarketingPage() {
  const [seats, setSeats] = useState({ seatsRemaining: null, soldOut: false });
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [pageError, setPageError] = useState(null);
  const [cardReady, setCardReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const paymentComponentRef = useRef(null);

  // onSuccess is registered once (inside the mount effect below) but needs
  // the *current* form values at confirm() time — a ref avoids the closure
  // capturing stale state from when the effect first ran.
  const formRef = useRef(form);
  useEffect(() => {
    formRef.current = form;
  }, [form]);

  useEffect(() => {
    axios
      .get(`${config.subscriptionServiceUrl}lifetime-seats`)
      .then(res => {
        const data = res.data || {};
        setSeats({
          seatsRemaining: typeof data.seatsRemaining === 'number' ? data.seatsRemaining : null,
          soldOut: !!data.soldOut,
        });
      })
      .catch(() => {
        // Best-effort — if this fails we just show the page without a seat count.
      });
  }, []);

  // Creates a Payment Intent for one installment (~$359 — Chargebee
  // authorizes/collects the item price's per-cycle amount, not the $1,077
  // total, since this is a real billing_cycles: 3 item price), then mounts
  // Chargebee's Payment Component against it, exactly once.
  // COMPONENT_LOAD_TIMEOUT_MS is a fallback: if it hasn't mounted by then,
  // show a clear message instead of leaving a broken/blank-looking form.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.Chargebee) {
      setPageError("Payment form isn't available right now — please refresh and try again.");
      return;
    }

    let cancelled = false;
    const cbInstance = window.Chargebee.init({
      site: config.chargebeeSiteName,
      publishableKey: config.chargebeePublishableKey,
    });

    const timeoutId = setTimeout(() => {
      if (cancelled || paymentComponentRef.current) return;
      setPageError('Payment form unavailable — please contact support.');
    }, COMPONENT_LOAD_TIMEOUT_MS);

    // Redeems an authorized Payment Intent for a real subscription. Reads
    // current form values via the ref (see its declaration above) since
    // onSuccess is registered once here but can fire long after, once the
    // shopper has actually typed into the form.
    const finishSubscription = paymentIntentId => {
      const {
        firstName,
        lastName,
        email,
        addressLine1,
        city,
        state,
        zip,
        country,
      } = formRef.current;
      axios
        .post(`${config.subscriptionServiceUrl}checkout/subscribe-with-intent`, {
          email: email.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          addressLine1: addressLine1.trim(),
          city: city.trim(),
          state: state.trim(),
          zip: zip.trim(),
          country: country || 'US',
          plan: PLAN_ID,
          paymentIntentId,
        })
        .then(res => {
          const data = res.data || {};
          if (!data.ok || !data.subscriptionId) {
            throw new Error(data.message || 'subscribe_failed');
          }
          // Same post-checkout flow /checkout-plan/:tier uses (see
          // CheckoutPlanPage.js): /checkout/complete auto-creates the
          // Sharetribe account for this email, logs the coach in, and
          // reconciles the Chargebee subscription metadata onto that new
          // account via /chargebee/link-subscription (server/api/auth/
          // completeCheckoutSignup.js) — the webhook that already fired for
          // this subscription found no matching Sharetribe user yet and
          // gave up permanently, so this step is what actually grants
          // access. Reusing it here rather than a page-local success
          // screen, so an installment purchase behaves identically to a
          // full-price one from this point on.
          window.location.href = `${window.location.origin}/checkout/complete?subscriptionId=${encodeURIComponent(
            data.subscriptionId
          )}`;
        })
        .catch(err => {
          setSubmitting(false);
          setPageError(
            (err && err.response && err.response.data && err.response.data.message) ||
              (err && err.message) ||
              "We couldn't process that card. Please check the details and try again."
          );
        });
    };

    const mountPaymentComponent = paymentIntent => {
      const components = cbInstance.components({});
      const paymentComponent = components.create(
        'payment',
        {
          paymentIntent,
          layout: { type: 'tab' },
          paymentMethods: { allowed: ['card'] },
        },
        {
          onError: error => {
            setSubmitting(false);
            setPageError(
              (error && error.message) || "We couldn't process that card. Please check the details and try again."
            );
          },
          onSuccess: authorizedIntent => {
            finishSubscription(authorizedIntent.id);
          },
        }
      );

      return paymentComponent.mount('#get-started-marketing-payment-component').then(mounted => {
        if (cancelled) return;
        if (!mounted) throw new Error('mount_failed');
        paymentComponentRef.current = paymentComponent;
        clearTimeout(timeoutId);
        setCardReady(true);
        // Mounting can finish after COMPONENT_LOAD_TIMEOUT_MS already fired
        // the "unavailable" error below (slow network/cold load) — clear it
        // now that the card form is genuinely ready, otherwise the stale
        // error banner (and the submit button it disables) get stuck even
        // though the form works fine.
        setPageError(null);
      });
    };

    axios
      .post(`${config.subscriptionServiceUrl}payment-intent`, { plan: PLAN_ID })
      .then(res => {
        if (cancelled) return;
        const paymentIntent = res.data && res.data.payment_intent;
        if (!paymentIntent) throw new Error('payment_intent_failed');
        return mountPaymentComponent(paymentIntent);
      })
      .catch(() => {
        if (!cancelled) {
          clearTimeout(timeoutId);
          setPageError('Payment form unavailable — please contact support.');
        }
      });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, []);

  // confirm() doesn't validate the form itself and doesn't return the
  // result directly — outcomes arrive via the onSuccess/onError callbacks
  // registered when the Payment Component was created (see the mount
  // effect above), which is where the actual subscribe call happens.
  const handleSubmit = async e => {
    e.preventDefault();
    setPageError(null);

    const errors = {};
    if (!form.firstName.trim()) errors.firstName = 'Required';
    if (!form.lastName.trim()) errors.lastName = 'Required';
    if (!EMAIL_RE.test(form.email.trim())) errors.email = 'Enter a valid email';
    if (!form.addressLine1.trim()) errors.addressLine1 = 'Required';
    if (!form.city.trim()) errors.city = 'Required';
    if (!form.state.trim()) errors.state = 'Required';
    if (!form.zip.trim()) {
      errors.zip = 'Required';
    } else if (!isValidPostalCode(form.zip.trim())) {
      errors.zip = 'Enter a valid ZIP / postal code';
    }
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    if (!paymentComponentRef.current || !cardReady) {
      setPageError("Payment form isn't ready yet — please wait a moment and try again.");
      return;
    }

    setSubmitting(true);
    const isValid = await paymentComponentRef.current.validate();
    if (!isValid) {
      // validate() has already surfaced inline errors inside the component.
      setSubmitting(false);
      return;
    }
    await paymentComponentRef.current.confirm();
  };

  const topbar = (
    <div className={css.topbar}>
      <a className={css.topbarHome} href="/">
        <img className={css.topbarLogo} src={coachLockupLogo} alt="Viveka" />
      </a>
    </div>
  );

  return (
    <PageWrapper>
      {topbar}
      <div className={css.base}>
        <div className={css.checkoutLayout}>
          <div className={css.leftCol}>
            <span className={css.badge}>Viveka First 100</span>
            <h1 className={css.title}>Verified Coach — Lifetime</h1>
            <div className={css.price}>
              {INSTALLMENT_AMOUNT_LABEL}
              <span className={css.priceUnit}>/mo for 3 months</span>
            </div>
            <div className={css.totalNote}>Total {TOTAL_LABEL} — no interest, no hidden fees</div>
            {seats.seatsRemaining !== null && !seats.soldOut && (
              <div className={css.seatNote}>{seats.seatsRemaining} of 100 seats remaining</div>
            )}
            <p className={css.description}>
              <span className={css.descriptionKicker}>You're Claiming a Founding Seat: </span>
              Pay for lifetime Verified Coach access in 3 interest-free monthly payments instead of all at once.
              Your full Verified Coach access starts today, on your first payment. Once your third payment clears,
              your account converts to permanent Lifetime status — no monthly fee, no renewal, no price changes, no
              matter how the platform grows.
            </p>
            <ul className={css.list}>
              {FEATURES.map(feature => (
                <li key={feature.title}>
                  <span className={css.featureText}>
                    <span className={css.featureTitle}>{feature.title}</span> — {feature.detail}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className={css.rightCol}>
            <div className={css.summaryCard}>
              <h2 className={css.summaryTitle}>Start with your first payment</h2>

              {pageError && <div className={css.checkoutError}>{pageError}</div>}

              {seats.soldOut ? (
                <div className={css.soldOut}>This plan is sold out.</div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className={css.formRow}>
                    <div className={css.formField}>
                      <label className={css.fieldLabel} htmlFor="gsm-first-name">
                        First name
                      </label>
                      <input
                        id="gsm-first-name"
                        className={css.textInput}
                        value={form.firstName}
                        onChange={e => {
                          const value = e.target.value;
                          setForm(f => ({ ...f, firstName: value }));
                        }}
                      />
                      {formErrors.firstName && <div className={css.fieldError}>{formErrors.firstName}</div>}
                    </div>
                    <div className={css.formField}>
                      <label className={css.fieldLabel} htmlFor="gsm-last-name">
                        Last name
                      </label>
                      <input
                        id="gsm-last-name"
                        className={css.textInput}
                        value={form.lastName}
                        onChange={e => {
                          const value = e.target.value;
                          setForm(f => ({ ...f, lastName: value }));
                        }}
                      />
                      {formErrors.lastName && <div className={css.fieldError}>{formErrors.lastName}</div>}
                    </div>
                  </div>

                  <div className={css.formField}>
                    <label className={css.fieldLabel} htmlFor="gsm-email">
                      Email
                    </label>
                    <input
                      id="gsm-email"
                      type="email"
                      className={css.textInput}
                      value={form.email}
                      onChange={e => {
                        const value = e.target.value;
                        setForm(f => ({ ...f, email: value }));
                      }}
                    />
                    {formErrors.email && <div className={css.fieldError}>{formErrors.email}</div>}
                  </div>

                  <div className={css.formField}>
                    <label className={css.fieldLabel} htmlFor="gsm-address">
                      Billing address
                    </label>
                    <input
                      id="gsm-address"
                      className={css.textInput}
                      value={form.addressLine1}
                      onChange={e => {
                        const value = e.target.value;
                        setForm(f => ({ ...f, addressLine1: value }));
                      }}
                      placeholder="Street address"
                    />
                    {formErrors.addressLine1 && <div className={css.fieldError}>{formErrors.addressLine1}</div>}
                  </div>

                  <div className={css.formField}>
                    <label className={css.fieldLabel} htmlFor="gsm-country">
                      Country
                    </label>
                    <select
                      id="gsm-country"
                      className={css.textInput}
                      value={form.country || 'US'}
                      onChange={e => {
                        const value = e.target.value;
                        setForm(f => ({ ...f, country: value }));
                      }}
                    >
                      {COUNTRIES.map(c => (
                        <option key={c.code} value={c.code}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className={css.formRow}>
                    <div className={css.formField}>
                      <label className={css.fieldLabel} htmlFor="gsm-city">
                        City
                      </label>
                      <input
                        id="gsm-city"
                        className={css.textInput}
                        value={form.city}
                        onChange={e => {
                          const value = e.target.value;
                          setForm(f => ({ ...f, city: value }));
                        }}
                      />
                      {formErrors.city && <div className={css.fieldError}>{formErrors.city}</div>}
                    </div>
                    <div className={css.formField}>
                      <label className={css.fieldLabel} htmlFor="gsm-state">
                        State
                      </label>
                      <input
                        id="gsm-state"
                        className={css.textInput}
                        value={form.state}
                        onChange={e => {
                          const value = e.target.value;
                          setForm(f => ({ ...f, state: value }));
                        }}
                      />
                      {formErrors.state && <div className={css.fieldError}>{formErrors.state}</div>}
                    </div>
                    <div className={css.formField}>
                      <label className={css.fieldLabel} htmlFor="gsm-zip">
                        ZIP / Postal code
                      </label>
                      <input
                        id="gsm-zip"
                        className={css.textInput}
                        value={form.zip}
                        onChange={e => {
                          const value = e.target.value;
                          setForm(f => ({ ...f, zip: value }));
                        }}
                      />
                      {formErrors.zip && <div className={css.fieldError}>{formErrors.zip}</div>}
                    </div>
                  </div>

                  <div className={css.formField}>
                    <label className={css.fieldLabel}>Payment details</label>
                    <div className={css.paymentComponentWrap}>
                      {/* Always rendered (never display:none) so Chargebee's
                          iframe has real dimensions to mount into — the
                          spinner overlays on top instead of replacing it. */}
                      <div id="get-started-marketing-payment-component" className={css.paymentComponentMount} />
                      {!cardReady && !pageError && (
                        <div className={css.paymentLoading}>
                          <span className={css.paymentSpinner} />
                          Loading secure payment form…
                        </div>
                      )}
                    </div>
                  </div>

                  <button type="submit" className={css.submitBtn} disabled={submitting || !!pageError}>
                    {submitting ? 'Processing…' : `Pay ${INSTALLMENT_AMOUNT_LABEL} & Start`}
                  </button>
                  <p className={css.finePrint}>
                    You'll be charged {INSTALLMENT_AMOUNT_LABEL} today, then {INSTALLMENT_AMOUNT_LABEL} automatically
                    in each of the next 2 months ({TOTAL_LABEL} total). Full Verified Coach access starts
                    immediately.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
}

export default GetStartedMarketingPage;
